import { Accordion } from "@base-ui/react/accordion";
import type { EarningStatus } from "@biteasy/contracts";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronDown, Filter, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/catalyst/Button";
import { Field, Label } from "@/components/catalyst/Fieldset";
import { Input } from "@/components/catalyst/Input";
import { Select } from "@/components/catalyst/Select";
import { orpc } from "@/orpc/client";
import { cn } from "@/util/string";

const STATUS_OPTIONS: { value: EarningStatus | ""; label: string }[] = [
	{ value: "", label: "All Statuses" },
	{ value: "accrued", label: "Accrued" },
	{ value: "processing", label: "Processing" },
	{ value: "paid", label: "Paid" },
	{ value: "void", label: "Void" },
];

export interface EarningsFilters {
	appId?: string;
	partnerId?: string;
	status?: EarningStatus;
	amountMin?: number;
	amountMax?: number;
	createdAfter?: Date;
	createdBefore?: Date;
}

interface EarningsFiltersBarProps {
	onFiltersChange: (filters: EarningsFilters) => void;
}

export function EarningsFiltersBar({
	onFiltersChange,
}: EarningsFiltersBarProps) {
	// Local state for accordion - prevents closing on filter changes
	const [isOpen, setIsOpen] = useState(false);

	const navigate = useNavigate();
	const search = useSearch({ from: "/_app/finances/" });

	const appId = search.eApp;
	const partnerId = search.ePartner;
	const status = search.eStatus;
	const amountMin = search.eMin;
	const amountMax = search.eMax;
	const createdAfter = search.eAfter;
	const createdBefore = search.eBefore;

	const setFilter = (updates: Record<string, unknown>) => {
		navigate({
			to: "/finances",
			search: (prev) => ({
				...prev,
				...updates,
				tab: prev.tab as "earnings" | "payouts" | "payment_methods",
			}),
		});
	};

	// Local state for amount inputs (to avoid constant API calls while typing)
	const [localAmountMin, setLocalAmountMin] = useState(
		amountMin ? String(amountMin / 100) : "",
	);
	const [localAmountMax, setLocalAmountMax] = useState(
		amountMax ? String(amountMax / 100) : "",
	);

	// Fetch apps and partners for dropdowns
	const { data: apps } = useQuery(orpc.apps.list.queryOptions());
	const { data: partners } = useQuery(
		orpc.partners.list.queryOptions({ input: {} }),
	);

	const hasActiveFilters =
		appId ||
		partnerId ||
		status ||
		amountMin ||
		amountMax ||
		createdAfter ||
		createdBefore;

	const activeFilterCount = [
		appId,
		partnerId,
		status,
		amountMin,
		amountMax,
		createdAfter,
		createdBefore,
	].filter(Boolean).length;

	useEffect(() => {
		const filters: EarningsFilters = {};

		if (appId) filters.appId = appId;
		if (partnerId) filters.partnerId = partnerId;
		if (status) filters.status = status as EarningStatus;
		if (amountMin) filters.amountMin = amountMin;
		if (amountMax) filters.amountMax = amountMax;
		if (createdAfter) filters.createdAfter = new Date(createdAfter);
		if (createdBefore) filters.createdBefore = new Date(createdBefore);

		onFiltersChange(filters);
	}, [
		appId,
		partnerId,
		status,
		amountMin,
		amountMax,
		createdAfter,
		createdBefore,
		onFiltersChange,
	]);

	const handleAmountMinBlur = () => {
		const value = localAmountMin
			? Math.round(Number.parseFloat(localAmountMin) * 100)
			: undefined;
		setFilter({ eMin: value });
	};

	const handleAmountMaxBlur = () => {
		const value = localAmountMax
			? Math.round(Number.parseFloat(localAmountMax) * 100)
			: undefined;
		setFilter({ eMax: value });
	};

	const handleClearFilters = () => {
		navigate({
			to: "/finances",
			search: (prev) => ({
				...prev,
				tab: prev.tab as "earnings" | "payouts" | "payment_methods",
				eApp: undefined,
				ePartner: undefined,
				eStatus: undefined,
				eMin: undefined,
				eMax: undefined,
				eAfter: undefined,
				eBefore: undefined,
			}),
		});
		setLocalAmountMin("");
		setLocalAmountMax("");
	};

	return (
		<Accordion.Root
			className="mt-6"
			value={isOpen ? ["filters"] : []}
			onValueChange={(value) => setIsOpen(value.includes("filters"))}
		>
			<Accordion.Item
				value="filters"
				className="rounded-xl bg-base-200/30 ring-1 ring-base-content/5 overflow-hidden"
			>
				<Accordion.Trigger className="group flex w-full items-center justify-between gap-4 px-5 py-4 text-sm font-medium text-base-content hover:bg-base-content/5 cursor-pointer">
					<div className="flex items-center gap-2">
						<Filter className="size-4" />
						<span>Filters</span>
						{activeFilterCount > 0 && (
							<span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
								{activeFilterCount}
							</span>
						)}
					</div>
					<ChevronDown
						className={cn(
							"size-4 text-base-content/50 transition-transform duration-300",
							"group-data-panel-open:rotate-180 group-data-panel-open:text-primary",
						)}
					/>
				</Accordion.Trigger>
				<Accordion.Panel className="h-(--accordion-panel-height) overflow-hidden transition-[height] duration-300 ease-out data-starting-style:h-0 data-ending-style:h-0">
					<div className="px-5 pb-5 pt-2">
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-4">
							{/* App Filter */}
							<Field>
								<Label>App</Label>
								<Select
									value={appId ?? ""}
									onChange={(e) => {
										setFilter({ eApp: e.target.value || undefined });
									}}
								>
									<option value="">All Apps</option>
									{apps?.map((app) => (
										<option key={app.id} value={app.id}>
											{app.name}
										</option>
									))}
								</Select>
							</Field>

							{/* Partner Filter */}
							<Field>
								<Label>Partner</Label>
								<Select
									value={partnerId ?? ""}
									onChange={(e) => {
										setFilter({ ePartner: e.target.value || undefined });
									}}
								>
									<option value="">All Partners</option>
									{partners?.map((partner) => (
										<option key={partner.id} value={partner.id}>
											{partner.name}
										</option>
									))}
								</Select>
							</Field>

							{/* Status Filter */}
							<Field>
								<Label>Status</Label>
								<Select
									value={status ?? ""}
									onChange={(e) => {
										setFilter({ eStatus: e.target.value || undefined });
									}}
								>
									{STATUS_OPTIONS.map((opt) => (
										<option key={opt.value} value={opt.value}>
											{opt.label}
										</option>
									))}
								</Select>
							</Field>

							{/* Amount Range */}
							<Field>
								<Label>Amount Range</Label>
								<div data-slot="control" className="flex items-center gap-2">
									<Input
										type="number"
										placeholder="Min"
										value={localAmountMin}
										onChange={(e) => setLocalAmountMin(e.target.value)}
										onBlur={handleAmountMinBlur}
										className="w-full"
									/>
									<span className="text-base-content/40">—</span>
									<Input
										type="number"
										placeholder="Max"
										value={localAmountMax}
										onChange={(e) => setLocalAmountMax(e.target.value)}
										onBlur={handleAmountMaxBlur}
										className="w-full"
									/>
								</div>
							</Field>

							{/* Created After */}
							<Field>
								<Label>Created After</Label>
								<Input
									type="date"
									value={
										createdAfter
											? new Date(createdAfter).toISOString().split("T")[0]
											: ""
									}
									onChange={(e) => {
										setFilter({
											eAfter: e.target.value
												? new Date(e.target.value).toISOString()
												: undefined,
										});
									}}
								/>
							</Field>

							{/* Created Before */}
							<Field>
								<Label>Created Before</Label>
								<Input
									type="date"
									value={
										createdBefore
											? new Date(createdBefore).toISOString().split("T")[0]
											: ""
									}
									onChange={(e) => {
										setFilter({
											eBefore: e.target.value
												? new Date(e.target.value).toISOString()
												: undefined,
										});
									}}
								/>
							</Field>
						</div>

						<div className="mt-4 flex justify-end">
							{hasActiveFilters && (
								<Button outline onClick={handleClearFilters}>
									<X className="size-3 mr-1" />
									Clear Filters
								</Button>
							)}
						</div>
					</div>
				</Accordion.Panel>
			</Accordion.Item>
		</Accordion.Root>
	);
}
